import { ILineItemStyle } from "@faire/retailer-visitor-shared/components/getLineItemColorByStyleAndSurface";
import { getMarketEventMatchedDiscountBps } from "@faire/retailer-visitor-shared/serialized-data/getMarketEventMatchedDiscountBps";
import { getNextOrActiveEvent } from "@faire/retailer-visitor-shared/serialized-data/getNextOrActiveEvent";
import { useSettingDecentralizePageData } from "@faire/retailer-visitor-shared/settings/useSettingDecentralizePageData";
import { useClock } from "@faire/web--source/common/useClock";
import { FaireMarketTheme } from "@faire/web--source/slate/Theme/EventsTheme";
import { useQuery as useMarketDataQuery } from "@faire/web-api--source/endpoints/www/api/page-data/market-data/get-hooks";
import { PromotionalEventType } from "@faire/web-api--source/indigofair/common/PromotionalEventType";
import { ICurrentPromotionalEventResponse } from "@faire/web-api--source/indigofair/data/ICurrentPromotionalEventResponse";
import { toZonedTime } from "date-fns-tz";
import { isAfter } from "date-fns/isAfter";
import { useCallback, useMemo } from "react";

import PromotionalEventGroup = ICurrentPromotionalEventResponse.IPromotionalEvent.PromotionalEventGroup;

type EventType = keyof typeof PromotionalEventType;
const MARKET_EVENT_MATCHED_DISCOUNT_BPS_DEFAULT = 500;

/**
 * TODO: This hook's argument is unused.
 * It's here to satisfy the type checker and will be removed in the followup PR.
 */
export const useEventsStore = (_: unknown[]) => {
  const shouldDecentralizePageData = useSettingDecentralizePageData();
  const { data } = useMarketDataQuery({
    staleTime: Infinity,
    refetchOnMount: false,
    enabled: shouldDecentralizePageData,
  });

  const nextOrActive = shouldDecentralizePageData
    ? data?.next_or_active_promotional_event
    : getNextOrActiveEvent();
  const marketEventMatchedDiscountBps = shouldDecentralizePageData
    ? (data?.market_event_matched_discount_bps ??
      MARKET_EVENT_MATCHED_DISCOUNT_BPS_DEFAULT)
    : getMarketEventMatchedDiscountBps();

  const isMarketEvent =
    nextOrActive?.event?.event_group === PromotionalEventGroup.MARKET;

  const eventLineItemStyle = isMarketEvent
    ? ILineItemStyle.MARKET
    : ILineItemStyle.FAIRE_FASHION_WEEK_DISCOUNT;

  const nextOrActiveEventTheme = useMemo(() => {
    switch (nextOrActive?.event?.event_group) {
      case PromotionalEventGroup.MARKET:
      default:
        return FaireMarketTheme;
    }
  }, [nextOrActive]);

  const eventData = useCallback(
    (eventType: EventType): ICurrentPromotionalEventResponse | undefined => {
      return nextOrActive?.event?.event_type === eventType
        ? nextOrActive
        : undefined;
    },
    [nextOrActive]
  );

  const { now } = useClock();

  const isEventExpired = useCallback(
    (eventType: EventType): boolean => {
      const eventData = nextOrActive;
      if (!eventData || eventData?.event?.event_type !== eventType) {
        return true;
      }
      const endAt = eventData.end_at;
      if (endAt === undefined) {
        // assuming an event with undefined end date always live
        return false;
      }
      return isAfter(now, endAt);
    },
    [nextOrActive, now]
  );

  return {
    nextOrActive,
    isMarketEvent,
    eventLineItemStyle,
    nextOrActiveEventTheme,
    eventData,
    isEventExpired,
    unsnapFromPT,
    marketEventMatchedDiscountBps,
  };
};

const PT_TIMEZONE = "America/Los_Angeles";

const unsnapFromPT = (timeUtc: number): number => {
  return toZonedTime(timeUtc, PT_TIMEZONE).valueOf();
};
